import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, ArrowDown } from 'lucide-react';

interface HeroProps {
  name: string;
  role: string;
  subtitle: string;
}

const Hero: React.FC<HeroProps> = ({ name, role, subtitle }) => {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-70 [background-image:radial-gradient(circle,rgba(0,0,0,0.045)_1px,transparent_1px)] dark:[background-image:radial-gradient(circle,rgba(255,255,255,0.045)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:linear-gradient(to_bottom,black_30%,transparent_100%)]"
      />
      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-32 pb-20 sm:pt-40 sm:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          <div className="space-y-4">
            <div className="font-mono text-[11px] tracking-[0.2em] text-neutral-500 dark:text-neutral-500 uppercase flex items-center gap-3">
              <span className="h-px w-6 bg-neutral-300 dark:bg-neutral-700" />
              <span>BO-001 · Senior Engineer · TR</span>
            </div>

            <motion.h1
              className="font-sans font-medium text-5xl sm:text-6xl md:text-7xl tracking-[-0.02em] text-neutral-900 dark:text-neutral-50 leading-[1.02]"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            >
              {name}
            </motion.h1>

            <motion.p
              className="font-mono text-xs sm:text-sm uppercase tracking-[0.14em] text-neutral-600 dark:text-neutral-400 pt-1"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              {role} <span className="text-neutral-400 dark:text-neutral-600">·</span>{' '}
              <span className="text-neutral-500 dark:text-neutral-500">Türkiye</span>
            </motion.p>
          </div>

          <motion.p
            className="text-base sm:text-lg leading-relaxed text-neutral-600 dark:text-neutral-400 max-w-2xl"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            {subtitle}
          </motion.p>

          <motion.div
            className="flex flex-wrap items-center gap-3 pt-2"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent hover:bg-accent-hover text-neutral-950 font-mono text-[11px] uppercase tracking-[0.14em] font-medium transition-colors"
            >
              Get in touch
            </a>
            <a
              href="#experience"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-neutral-300 dark:border-neutral-700 text-neutral-800 dark:text-neutral-200 font-mono text-[11px] uppercase tracking-[0.14em] hover:border-neutral-900 dark:hover:border-neutral-300 transition-colors"
            >
              View experience
              <ArrowDown className="w-3 h-3" />
            </a>
          </motion.div>

          <motion.div
            className="flex items-center gap-5 pt-2 text-neutral-500 dark:text-neutral-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <a
              href="mailto:bugraozdilek@gmail.com"
              className="hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              aria-label="Email"
            >
              <Mail size={18} />
            </a>
            <a
              href="https://www.linkedin.com/in/bugra-ozdilek123/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
            <a
              href="https://github.com/bugraOzd"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              aria-label="GitHub"
            >
              <Github size={18} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
